'use client';

import React, { useEffect, useRef } from 'react';

const faqs = [
  {
    q: 'Jak długo trwa detailing samochodu?',
    a: 'Czas zależy od wybranego pakietu. Pranie tapicerki to ok. 2–4 godziny, korekta lakieru może zająć cały dzień. Dokładny czas podamy przy wycenie.',
  },
  {
    q: 'Czy muszę zostawić auto na cały dzień?',
    a: 'W przypadku kompleksowego detailingu tak — chcemy mieć czas na dokładną pracę. Przy prostszych usługach możliwa jest wizyta kilkugodzinna.',
  },
  {
    q: 'Jak umówić termin?',
    a: 'Najprościej zadzwonić pod numer 731 655 388 lub napisać wiadomość na Facebook. Odpowiadamy szybko i elastycznie dopasowujemy termin.',
  },
  {
    q: 'Czy robicie wyceny?',
    a: 'Tak — każda wycena jest bezpłatna i indywidualna. Skontaktuj się z nami, opisz stan auta, a przygotujemy ofertę.',
  },
];

export default function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal-hidden, .reveal-scale');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const toggleFaq = (e: React.MouseEvent<HTMLButtonElement>) => {
    const item = e.currentTarget.closest('.faq-item') as HTMLElement;
    const isOpen = item.classList.contains('open');
    // Close all
    document.querySelectorAll('.faq-item').forEach((el) => el.classList.remove('open'));
    if (!isOpen) item.classList.add('open');
  };

  return (
    <section ref={sectionRef} id="kontakt" className="py-24 md:py-32 bg-kd-black relative overflow-hidden">
      {/* Glow */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-gradient-radial-cyan blur-3xl pointer-events-none opacity-30" />

      <div className="max-w-container mx-auto px-6 relative z-10">
        {/* Big CTA block */}
        <div className="relative overflow-hidden rounded-3xl mb-20 reveal-scale" style={{ background: 'linear-gradient(135deg, #0d1f2d 0%, #0a1520 50%, #0A0A0A 100%)' }}>
          {/* Border glow */}
          <div className="absolute inset-0 rounded-3xl border border-kd-cyan/20 pointer-events-none" style={{ animation: 'borderPulse 3s ease-in-out infinite' }} />
          {/* Decorative circles */}
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full border border-kd-cyan/10 pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-60 h-60 rounded-full border border-kd-cyan/10 pointer-events-none" />

          <div className="relative z-10 px-8 py-16 md:px-16 md:py-20 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-kd-cyan/30 bg-kd-cyan/5 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-kd-cyan opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-kd-cyan" />
              </span>
              <span className="text-kd-cyan text-xs font-body font-semibold uppercase tracking-widest">
                Umów się już dziś
              </span>
            </div>

            <h2 className="font-heading font-black uppercase text-white mb-4" style={{ fontSize: 'clamp(2.5rem, 7vw, 6rem)', lineHeight: '0.95' }}>
              ZADZWOŃ I UMÓW<br />
              <span className="gradient-text">TERMIN!</span>
            </h2>

            <p className="text-kd-muted font-body text-lg max-w-xl mx-auto mb-10 leading-relaxed">
              Bezpłatna wycena, szybki termin, profesjonalna obsługa.
              Działamy w Sieprawiu i okolicach Małopolski.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a
                href="tel:+48731655388"
                className="btn-primary relative inline-flex items-center justify-center gap-3 px-10 py-5 rounded-2xl font-heading font-black text-white text-2xl uppercase tracking-wider z-10"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                731 655 388
              </a>
              <a
                href="https://www.facebook.com/pranietapicerkisiepraw/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline inline-flex items-center justify-center gap-3 px-8 py-5 rounded-2xl font-heading font-bold text-white text-xl uppercase tracking-wider"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                Facebook
              </a>
            </div>

            {/* Info row */}
            <div className="flex flex-wrap justify-center gap-8 border-t border-kd-border pt-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-kd-cyan/10 flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3AA4DE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-heading font-bold text-white text-sm uppercase">Lokalizacja</p>
                  <p className="text-kd-muted text-xs font-body">ul. Kwiatowa 61, Siepraw</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-kd-cyan/10 flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3AA4DE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-heading font-bold text-white text-sm uppercase">Godziny</p>
                  <p className="text-kd-muted text-xs font-body">Pon–Sob: 8:00 – 18:00</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-kd-cyan/10 flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3AA4DE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-heading font-bold text-white text-sm uppercase">Telefon</p>
                  <p className="text-kd-muted text-xs font-body">731 655 388</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10 reveal-hidden">
            <h3 className="font-heading font-black uppercase text-white text-4xl mb-3">
              CZĘSTE <span className="gradient-text">PYTANIA</span>
            </h3>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={faq.q}
                className="faq-item service-card rounded-xl overflow-hidden reveal-hidden"
                style={{ transitionDelay: `${i * 0.08}s` }}
              >
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left focus:outline-none focus:ring-2 focus:ring-kd-cyan/30 rounded-xl"
                  onClick={toggleFaq}
                  aria-expanded="false"
                >
                  <span className="font-heading font-bold text-white text-lg uppercase tracking-wide pr-4">
                    {faq.q}
                  </span>
                  <div className="faq-icon shrink-0 w-8 h-8 rounded-full border border-kd-border flex items-center justify-center text-kd-cyan">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </div>
                </button>
                <div className="faq-content">
                  <div className="px-6 pb-5 text-kd-muted font-body text-sm leading-relaxed border-t border-kd-border pt-4">
                    {faq.a}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}